import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

const RecordingNavigationBlocker = ({
    onConfirm,
    onCancel,
    onThirdOption,
    // Optional "go back to what you were doing" action, shown first and
    // focused, so an accidental End (or an Enter that landed on it) costs
    // nothing (Toby, 2026-10-02).
    onKeepGoing,
    keepGoingLabel,
    message,
    confirmLabel,
    cancelLabel,
    thirdOptionLabel,
    // The upload kicked off by this dialog is still running. Every button goes
    // dead so a rep on a slow connection cannot queue the same work again by
    // clicking "Yes" repeatedly, which is what turned one stalled upload into
    // thirteen lost recordings on 2026-08-27.
    busy = false,
    // ...except a confirm that only navigates away. Leaving unmounts the screen,
    // which resets the store and aborts the upload cleanly, so it stays available
    // as the escape hatch while retries run.
    confirmEnabledWhileBusy = false
}) => {
    const overlayRef = useRef(null);
    const confirmDisabled = busy && !confirmEnabledWhileBusy;

    useEffect(() => {
        if (!busy) return;
        const overlay = overlayRef.current;
        if (!overlay) return;
        // A disabled button takes no focus, so the autoFocus below stops working
        // the moment the dialog goes busy and keystrokes would land on <body>,
        // outside the onKeyDown guard. Pull focus to the one button still live,
        // or to the overlay itself.
        const stillEnabled = overlay.querySelector('button:not([disabled])');
        (stillEnabled || overlay).focus();
    }, [busy]);

    const buttonState = (disabled) => (disabled
        ? { cursor: 'wait', opacity: 0.6 }
        : { cursor: 'pointer', opacity: 1 });

    // Four choices do not fit on one row of a 450px dialog: the labels wrapped
    // word by word and the first button was pushed off the left edge (Toby,
    // 2026-10-02). Any dialog with three or more choices stacks them full width,
    // safe choice first, and the DOM order matches what is seen so Tab follows
    // it. The plain No / Yes dialog keeps its row.
    const stacked = !!onKeepGoing || !!onThirdOption;
    const base = {
        fontFamily: 'Montserrat, sans-serif',
        padding: stacked ? '11px 20px' : '8px 20px',
        borderRadius: '6px',
        fontSize: '14px',
        ...(stacked ? { width: '100%', whiteSpace: 'nowrap' } : {}),
    };

    const keepButton = onKeepGoing ? (
        <button
            key="keep"
            onClick={onKeepGoing}
            disabled={busy}
            autoFocus
            style={{ ...base, border: '1px solid #0784a7', background: '#fff', color: '#0784a7', fontWeight: 600, ...buttonState(busy) }}
        >
            {keepGoingLabel || 'Keep going'}
        </button>
    ) : null;

    const cancelButton = (
        <button
            key="cancel"
            onClick={onCancel}
            disabled={busy}
            autoFocus={!onKeepGoing}
            style={{
                ...base,
                border: '1px solid #e0e0e0',
                background: onThirdOption ? '#f0f0f0' : '#fff',
                color: '#333',
                fontWeight: onThirdOption ? 600 : 400,
                ...buttonState(busy),
            }}
        >
            {cancelLabel || 'No'}
        </button>
    );

    const thirdButton = onThirdOption ? (
        <button
            key="third"
            onClick={onThirdOption}
            disabled={busy}
            style={{ ...base, border: 'none', background: '#4a9bb5', color: '#fff', fontWeight: 600, ...buttonState(busy) }}
        >
            {thirdOptionLabel}
        </button>
    ) : null;

    const confirmButton = (
        <button
            key="confirm"
            onClick={onConfirm}
            disabled={confirmDisabled}
            style={{
                ...base,
                border: 'none',
                background: onThirdOption ? '#c0616b' : '#0784a7',
                color: '#fff',
                fontWeight: 600,
                ...buttonState(confirmDisabled),
            }}
        >
            {confirmLabel || 'Yes'}
        </button>
    );

    return createPortal(
        // Same modal discipline as NavigationBlocker (review, 2026-09-04): keys
        // stop at the dialog, focus lands on the safe answer, and the dialog
        // role lets page-level shortcuts see that a modal is open.
        <div
            ref={overlayRef}
            role="dialog"
            aria-modal="true"
            tabIndex={-1}
            onKeyDown={(e) => e.stopPropagation()}
            style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
        }}>
            <div style={{
                background: '#fff',
                borderRadius: '10px',
                width: '90%',
                maxWidth: '450px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                overflow: 'hidden',
            }}>
                <div style={{
                    padding: '24px',
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '15px',
                    color: '#1a2b3d',
                    lineHeight: '1.6',
                }}>
                    {busy
                        ? 'Uploading your recording. Please keep this tab open.'
                        : (message || 'Are you ready to see what Murphy says about your meeting?')}
                </div>
                <div style={{
                    padding: stacked ? '16px 24px 20px' : '12px 24px',
                    borderTop: '1px solid #e8e8e8',
                    display: 'flex',
                    ...(stacked
                        ? { flexDirection: 'column', alignItems: 'stretch', gap: '8px' }
                        : { justifyContent: 'flex-end', gap: '10px' }),
                }}>
                    {(stacked ? [keepButton, thirdButton, cancelButton, confirmButton] : [cancelButton, thirdButton, confirmButton])}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default RecordingNavigationBlocker;
